import { AsyncPipe, DecimalPipe, formatDate } from "@angular/common";
import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountMarketplace, AsinRanks } from "@front/m19-api-client";
import {
  AccountSelectionService,
  AuthService,
  getRankFromOption,
  RANK_OPTIONS_ARRAY,
  RANK_OPTIONS_DESC,
  RankOption,
} from "@front/m19-services";
import { DateAggregation } from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import {
  KeywordTrackingTimelineTableComponent,
  RankingDataRow,
} from "@m19-board/keyword-tracker/keyword-tracking-timeline/keyword-tracking-timeline-table/keyword-tracking-timeline-table.component";
import { TrackedAsinSelectionComponent } from "@m19-board/keyword-tracker/keyword-tracking-timeline/tracked-asin-selection/tracked-asin-selection.component";
import { CsvExportService, fieldExtractor, simpleField } from "@m19-board/services/csv-export.service";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ChartDataset, Point } from "chart.js";
import { BehaviorSubject, combineLatest, map, Observable, Subject } from "rxjs";
import { KeywordTrackerService } from "../../services/keyword-tracker.service";
import { ChartConfig, IChartComponent, IDateAggreationSwitchComponent, ISwitchButtonComponent } from "@front/m19-ui";

export type AsinRanksEx = AsinRanks & {
  hasParent: boolean;
  inCatalog: boolean;
  isTop10: boolean;
  childAsins: string[];
};

@UntilDestroy()
@Component({
  selector: "app-keyword-tracking-timeline",
  templateUrl: "./keyword-tracking-timeline.component.html",
  styleUrls: ["./keyword-tracking-timeline.component.scss"],
  imports: [
    TranslocoDirective,
    TrackedAsinSelectionComponent,
    AsyncPipe,
    KeywordTrackingTimelineTableComponent,
    ExportButtonComponent,
    ISwitchButtonComponent,
    IDateAggreationSwitchComponent,
    IChartComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeywordTrackingTimelineComponent implements OnInit, AfterViewInit {
  private keywordTrackerService = inject(KeywordTrackerService);
  private accSelectionService = inject(AccountSelectionService);
  private csvExportService = inject(CsvExportService);
  private authService = inject(AuthService);

  readonly dateAggregations = [DateAggregation.hourly, DateAggregation.daily, DateAggregation.weekly];

  // input observables
  selectedAccountMarketplace$: Observable<AccountMarketplace> =
    this.accSelectionService.singleAccountMarketplaceSelection$;
  asinRanks$ = this.keywordTrackerService.asinRanksFiltered$;
  rankingRowData = toSignal(this.keywordTrackerService.asinRankRowData$);
  tableView = toSignal(this.keywordTrackerService.tableView$, { requireSync: true });

  dates = toSignal(this.keywordTrackerService.dates$);
  locale = toSignal(this.authService.loggedUser$.pipe(map((u) => u.locale)));

  dateAggregation$ = new BehaviorSubject<DateAggregation>(DateAggregation.daily);
  searchTerm?: string;

  @ViewChild("datavizScroll") datavizScroll!: ElementRef;
  updateScroll: Subject<Event> = new Subject<Event>();

  hoveredAsin = signal<string | undefined>(undefined);

  selectedAccountMarketplace = toSignal(this.selectedAccountMarketplace$, { requireSync: true });
  dateAggregation = toSignal(this.dateAggregation$, { requireSync: true });
  asinRanks = toSignal(this.asinRanks$);
  rankOption = toSignal(this.keywordTrackerService.rankOption$, { requireSync: true });
  graphAsin = toSignal(this.keywordTrackerService.graphAsin$, { requireSync: true });
  hiddenAsins = toSignal(this.keywordTrackerService.hiddenAsins$, { requireSync: true, equal: () => false });
  groupByParent = toSignal(this.keywordTrackerService.groupByParent$, { requireSync: true });
  colors = toSignal(this.keywordTrackerService.asinColors$, { requireSync: true });

  filteredAsinRanks = computed(() => {
    const asinRanks = this.asinRanks() ?? [];
    const graphAsin = this.graphAsin();

    return asinRanks.filter((rank) => {
      const isParent = !rank?.hasParent && rank?.childAsins.length;
      const hideParent = isParent && !this.groupByParent();

      const isAsinManuallySelected = !graphAsin || graphAsin === rank.asin;
      const isAsinManuallyHidden = this.hiddenAsins().has(rank.asin!);
      return isAsinManuallySelected && !isAsinManuallyHidden && !hideParent;
    });
  });
  datasetLegends = computed<string[]>(() => {
    if (this.rankOption() === RankOption.BOTH) {
      return ["Organic", "Sponsored"];
    }
    return this.filteredAsinRanks().map((r) => r.asin!) ?? [];
  });

  datasetConfig = computed<ChartConfig<number, "timeSeriesRanking">>(() => {
    const legends = this.datasetLegends();

    const colors = this.rankOption() !== RankOption.BOTH ? this.colors() : undefined;

    return {
      dateAggregation: this.dateAggregation(),
      title: "Search terms rank",
      displayLegend: false,
      legends: legends,
      legendFilters: new Map(),
      aggFunctions: [],
      type: "timeSeriesRanking",
      metrics: [],
      reverseYAxis: true,
      colorsByLabel: colors,
      marketplace: this.selectedAccountMarketplace().marketplace,
      datasetOutlineIdentifier: (dataset: ChartDataset<"line" | "bar">) => dataset.label,
      stepSize: 1,
    };
  });

  data = computed<Point[][]>(() => {
    const asinRanks = this.filteredAsinRanks();
    const rankOption = this.rankOption();
    const graphAsin = this.graphAsin();

    let ranks: Point[][] = [];
    if (rankOption == RankOption.BOTH) {
      const asinRank: AsinRanksEx = asinRanks.filter((r) => r.asin === graphAsin)[0];
      if (!asinRank || !asinRank.ranks) return [];

      ranks.push(
        asinRank.ranks
          .map((r) => ({
            x: r.timestamp! * 1000,
            y: r.organic!,
          }))
          .filter((point) => point.y !== undefined),
      );
      ranks.push(
        asinRank.ranks
          .map((r) => ({
            x: r.timestamp! * 1000,
            y: r.sp!,
          }))
          .filter((point) => point.y !== undefined),
      );
    } else {
      for (const asinRank of asinRanks) {
        if (asinRank.ranks) {
          ranks.push(
            asinRank.ranks
              .map((r) => ({
                x: r.timestamp! * 1000,
                y: getRankFromOption(r, rankOption) ?? 0,
              }))
              .filter((point) => point.y !== 0),
          );
        }
      }
      ranks = ranks.map((r) => r.filter((point) => point.y !== undefined));
    }

    return ranks;
  });

  ngAfterViewInit(): void {
    this.asinRanks$.pipe(untilDestroyed(this)).subscribe((_) => {
      scrollToRight(this.datavizScroll);
    });
  }

  ngOnInit(): void {
    this.keywordTrackerService.selectedSearchTerm$.pipe(untilDestroyed(this)).subscribe((st) => {
      this.searchTerm = st?.searchTerm;
    });

    combineLatest([this.asinRanks$, this.keywordTrackerService.rankOption$])
      .pipe(untilDestroyed(this))
      .subscribe(([asinRanks, rankOption]) => {
        if (rankOption == RankOption.BOTH) {
          const selectedAsin = this.selectCorrectAsin(asinRanks);
          this.keywordTrackerService.toggleGraphAsin(selectedAsin, true);
        }
      });
  }

  selectRankOption(option: RankOption | null) {
    if (option) this.keywordTrackerService.setRankOption(option);
  }

  // when choosing 'BOTH' option, we want to :
  // - keep the ASIN selected if there is already one
  // - selected the first ASIN otherwise taking grouping into account
  selectCorrectAsin(ranks: AsinRanksEx[]): string {
    if (!this.graphAsin()) {
      if (this.groupByParent()) return ranks[0]?.asin ?? "";
      else
        for (const r of ranks) {
          if (r.hasParent) return r.asin ?? "";
        }
    }
    return this.graphAsin() ?? "";
  }

  setTableView(set: boolean) {
    this.keywordTrackerService.setTableView(set);
    if (set) {
      scrollToRight(this.datavizScroll);
    }
  }

  selectDateAggregation(selection: DateAggregation) {
    this.dateAggregation$.next(selection);
  }

  onAsinSelectorHover(asin: string) {
    if (this.graphAsin()) return;
    if (this.rankOption() == RankOption.BOTH) {
      return;
    }

    this.hoveredAsin.set(asin);
  }

  onAsinSelectorLeave() {
    if (this.rankOption() == RankOption.BOTH) {
      return;
    }
    this.hoveredAsin.set(undefined);
  }

  exportData() {
    const filename = `keyword-tracker_${this.searchTerm}_${this.formatRankOption(this.rankOption()!)}`;

    this.csvExportService.exportCsv<RankingDataRow>(
      {
        prefix: filename,
        marketplace: this.selectedAccountMarketplace().marketplace,
      },
      this.rankingRowData()!,
      [
        simpleField("asin"),
        ...this.dates()!.map((d) =>
          fieldExtractor(formatDate(d, "MM/dd", this.locale() ?? "en-US"), (r: RankingDataRow) => {
            return this.getRank(r, this.rankOption()!, d);
          }),
        ),
      ],
    );
  }

  private getRank(r: RankingDataRow, rankOption: RankOption, date: string): string {
    const decimalPipe = new DecimalPipe(this.locale() ?? "en-US");
    let rank: number | undefined;
    if (rankOption == RankOption.ORGANIC) {
      rank = r.timeline[date]?.organic?.rank;
    } else if (rankOption == RankOption.SPONSORED) {
      rank = r.timeline[date]?.sponsored?.rank;
    } else if (rankOption == RankOption.GLOBAL) {
      rank = r.timeline[date]?.global?.rank;
    } else {
      return (
        this.formatRank(decimalPipe, r.timeline[date]?.organic?.rank) +
        " / " +
        this.formatRank(decimalPipe, r.timeline[date]?.sponsored?.rank)
      );
    }

    return this.formatRank(decimalPipe, rank);
  }

  private formatRank(decimalPipe: DecimalPipe, rank: number | undefined): string {
    return rank ? (decimalPipe.transform(rank, "1.0-0") ?? "-") : "-";
  }

  private formatRankOption(rankOption: RankOption): string {
    switch (rankOption) {
      case RankOption.ORGANIC:
        return "Organic";
      case RankOption.SPONSORED:
        return "Sponsored";
      case RankOption.BOTH:
        return "Organic / Sponsored";
      case RankOption.GLOBAL:
        return "Global";
    }
  }

  protected readonly RankOption = RankOption;
  protected readonly RANK_OPTIONS_ARRAY = RANK_OPTIONS_ARRAY;
  protected readonly RANK_OPTIONS_DESC = RANK_OPTIONS_DESC;
}

export function scrollToRight(el: ElementRef) {
  setTimeout(() => {
    const element = el as ElementRef<HTMLDivElement>;
    if (!element?.nativeElement) return;
    const left = element.nativeElement.scrollWidth;
    if (left != undefined && left > 0) {
      element.nativeElement.scroll({ left: left, behavior: "smooth" });
    }
  });
}
